import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import gql from 'graphql-tag';
import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Container } from '../components/container/container';
import { MarketingOptin } from '../components/register-screens/marketing-optin';
import { NoTransactionalAccount } from '../components/register-screens/no-transactional-account';
import { Rules } from '../components/register-screens/rules';
import {
  AccountEntry,
  SelectAccount,
} from '../components/register-screens/select-account';
import { Terms } from '../components/register-screens/terms';
import {
  MeDocument,
  MeQuery,
  useCreatePaddleMutation,
  useMeQuery,
} from '../graphql/types';
import { color } from '../styles/mixins';
import { typography } from '../styles/typography';
import useActiveYear from '../hooks/useActiveYear';

const Center = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  ${typography.fonts.mercury(32)}
  color: ${color('gray80')};
`;

const sothebysGql = gql;

const AccountSelectorQuery = sothebysGql`
  query sothebysAccountsSelector {
    me {
      transactionalUserStatus: status {
        ... on TransactionalUserStatus {
          id
          activeAccounts
          partyId
        }
      }
      basicUserStatus: status {
        ... on BasicUserStatus {
          id
          partyId
        }
      }
      downgradedUserStatus: status {
        ... on DowngradedUserStatus {
          id
          partyId
          reason
        }
      }
      contact {
        hasIdentificationV2
      }
      accounts {
        id
        accountId
        accountStatus
        holderName
        bidStatus
        type
      }
    }
  }
`;

enum RegisterScreenState {
  LOADING = 'LOADING',
  NO_AUTH = 'NO_AUTH',
  NO_TRANSACTIONAL_ACCOUNTS = 'NO_TRANSACTIONAL_ACCOUNTS',
  ACCEPT_TERMS = 'ACCEPT_TERMS',
  SELECT_ACCOUNT = 'SELECT_ACCOUNT',
  MARKETING = 'MARKETING',
  RULES = 'RULES',
  REGISTERED = 'REGISTERED',
}

export function Register() {
  const { isAuthenticated, isLoading } = useAuth0();
  const [createPaddle] = useCreatePaddleMutation();
  const [searchParams] = useSearchParams();
  const accountSelector = useQuery(AccountSelectorQuery, {
    fetchPolicy: 'network-only',
  });
  const me = useMeQuery({
    fetchPolicy: 'cache-first',
  });
  const [paddleNumber, setPaddleNumber] = useState<number | null>(
    me.data?.me?.paddle?.number ?? null
  );

  const ACTIVE_YEAR = useActiveYear();

  const accounts: AccountEntry[] = accountSelector.data?.me?.accounts || [];
  const transactionalAccountIds =
    accountSelector.data?.me?.transactionalUserStatus?.activeAccounts || [];
  const transactionalAccounts = accounts.filter((a) =>
    transactionalAccountIds.includes(a.accountId)
  );

  // Figure out what onboarding screen to show
  const getScreenState = () => {
    if (isLoading || me.loading) {
      // We are waiting for auth and user data
      return RegisterScreenState.LOADING;
    }
    if (!isAuthenticated) {
      // Not authenticated, should redirect to login
      return RegisterScreenState.NO_AUTH;
    }
    if (accountSelector.loading) {
      // Still loading account selector from sothebys
      return RegisterScreenState.LOADING;
    }
    if (transactionalAccounts.length === 0) {
      // No transactional accounts for the user
      return RegisterScreenState.NO_TRANSACTIONAL_ACCOUNTS;
    }
    if (!me.data?.me?.licenseAgreedAt) {
      // User has not agreed to the license
      return RegisterScreenState.ACCEPT_TERMS;
    }
    if (!me.data?.me?.paddle) {
      // User has not selected an account yet
      return RegisterScreenState.SELECT_ACCOUNT;
    }
    // User is registered, show the auction rules
    return RegisterScreenState.REGISTERED;
  };

  const [screenState, setScreenState] = useState<RegisterScreenState>(
    getScreenState()
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screenState]);

  useEffect(() => {
    setScreenState(getScreenState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, me.loading, accountSelector.loading]);

  const onAccepted = () => {
    if (transactionalAccounts.length === 1) {
      return onAccountSelected(transactionalAccounts[0]);
    }
    setScreenState(RegisterScreenState.SELECT_ACCOUNT);
  };

  const onAccountSelected = (account: AccountEntry) => {
    createPaddle({
      update(cache, { data }) {
        const meCache = cache.readQuery<MeQuery>({
          query: MeDocument,
        });
        if (meCache) {
          cache.writeQuery({
            query: MeDocument,
            data: {
              ...meCache,
              me: {
                ...meCache.me,
                paddle: {
                  ...data?.createPaddle,
                },
              },
            },
          });
        }
      },
      variables: {
        accountId: account.accountId,
      },
      onCompleted(data) {
        setPaddleNumber(data.createPaddle?.number ?? null);
        setScreenState(RegisterScreenState.MARKETING);
      },
    });
  };

  const onMarketingComplete = () => {
    setScreenState(RegisterScreenState.RULES);
  };

  const screens = {
    [RegisterScreenState.LOADING]: <Center>Loading...</Center>,
    [RegisterScreenState.NO_AUTH]: <Navigate to="/login" />,
    [RegisterScreenState.NO_TRANSACTIONAL_ACCOUNTS]: (
      <NoTransactionalAccount
        clientData={accountSelector.data?.me}
        error={accountSelector.error}
      />
    ),
    [RegisterScreenState.ACCEPT_TERMS]: <Terms onAccept={onAccepted} />,
    [RegisterScreenState.SELECT_ACCOUNT]: (
      <SelectAccount
        onSelect={onAccountSelected}
        accounts={transactionalAccounts}
      />
    ),
    [RegisterScreenState.MARKETING]: (
      <MarketingOptin onComplete={onMarketingComplete} />
    ),
    [RegisterScreenState.RULES]: (
      <Rules
        paddleNumber={paddleNumber}
        startsAt={me?.data?.activeAuction?.startsAt}
      />
    ),
    [RegisterScreenState.REGISTERED]: searchParams.has('returnTo') ? (
      <Navigate
        to={decodeURIComponent(
          searchParams.get('returnTo') || `/${ACTIVE_YEAR}/lots`
        )}
      />
    ) : (
      <Navigate to={`/${ACTIVE_YEAR}/lots`} />
    ),
  };

  return <Container>{screens[screenState]}</Container>;
}
